/*
 *  CONSTANTS
 *  intended to be a single source of truth for constants that impact the UX
 */
export const brandPalette = {
    green: {
        300: 'hsl(151, 55%, 75%)', // light-green
        400: 'hsl(161, 50%, 50%)', // mid-green
        '400a': 'hsla(161, 50%, 50%, 0.8)', // mid-green
        500: 'hsl(172, 100%, 30%)', // green
        700: 'hsl(172, 100%, 24%)', // green-bifido
        '700a': 'hsla(172, 100%, 24%, 0.8)', // green-bifido
    },
    white: {
        100: 'hsl(72, 26%, 96%)', // off-white
    },
    grey: {
        0: 'hsl(255, 7%, 99%)', // grey-white
        100: 'hsl(255, 7%, 96%)', // grey-xlite
        200: 'hsl(255, 7%, 91%)', // grey-xlite
        300: 'hsl(255, 7%, 76%)', // grey-lite
        400: 'hsl(255, 7%, 66%)', // grey-mid
        500: 'hsl(255, 7%, 56%)', // grey
        600: 'hsl(255, 7%, 46%)', // grey-600
        700: 'hsl(255, 7%, 36%)', // grey-xdark
        800: 'hsl(255, 7%, 24%)', // slate
        900: 'hsl(255, 7%, 19%)', // black
    },
    orange: {
        300: 'hsl(32, 91%, 73%)', // light-orange
        '300a': 'hsla(32, 91%, 73%, 0.8)', // light-orange
        500: 'hsl(38, 84%, 56%)', // orange
        '500a': 'hsla(38, 84%, 56%, 0.8)', // orange
    },
    red: {
        300: 'hsl(360, 100%, 80%)', // pink
        '300a': 'hsla(360, 100%, 80%, 0.8)', // pink
        400: 'hsl(360, 80%, 70%)', // pink
        500: 'hsl(360, 63%, 61%)', // red
        '500a': 'hsla(360, 63%, 61%, 0.8)', // red
    },
    yellow: {
        300: 'hsl(53, 80%, 74%)', // yellow
        '300a': 'hsla(53, 80%, 74%, 0.8)', // yellow
        400: 'hsl(53, 80%, 62%)', // yellow
        500: 'hsl(53, 84%, 50%)', // yellow dark
        '500a': 'hsla(53, 84%, 50%, 0.8)', // yellow dark
    },
    indigo: {
        300: 'hsl(231, 100%, 84%)', // née light-purple
        '300a': 'hsla(231, 100%, 84%, 0.8)', // née light-purple
        500: 'hsl(230, 40%, 55%)', // née purple
        '500a': 'hsla(230, 40%, 55%, 0.8)', // née purple
    },
    purple: {
        300: 'hsl(271, 100%, 84%)',
        500: 'hsl(271, 40%, 55%)',
    },
    blue: {
        50: 'hsl(202, 82%, 99%)', // light-blue
        100: 'hsl(202, 82%, 89%)', // light-blue
        200: 'hsl(202, 82%, 79%)', // blue-lite
        300: 'hsl(202, 82%, 69%)', // light-blue
        400: 'hsl(202, 82%, 59%)',
        500: 'hsl(202, 82%, 49%)', // blue
        600: 'hsl(202, 82%, 39%)', // link-blue
        700: 'hsl(202, 82%, 29%)', // blue
        800: 'hsl(202, 82%, 19%)', // blue
        900: 'hsl(202, 82%, 9%)', // blue
    },
    brown: {
        300: 'hsl(28, 44%, 65%)', // light brown
        '300a': 'hsla(28, 44%, 65%)', // light brown
        500: 'hsl(28, 44%, 41%)', // dark brown
        '500a': 'hsla(28, 44%, 41%, 0.8)', // dark brown
    },
    cadet: {
        300: 'hsl(202, 65%, 76%)',
        '300a': 'hsla(202, 65%, 76%, 0.8)',
        500: 'hsl(202, 55%, 52%)',
        '500a': 'hsla(202, 55%, 52%, 0.8)',
    },
};
export const brandColors = {
    ...brandPalette,
    gray: brandPalette.grey,
    clear: 'hsla(231, 50%, 50%, 0)',
    white: brandPalette.grey[0],
    offwhite: brandPalette.white[100],
    slate: brandPalette.grey[800],
    black: brandPalette.grey[900],
    bifidoGreen: brandPalette.green[700],
};
export const palette = {
    fg: brandColors.black,
    bg: brandColors.white,
    paper: brandColors.offwhite,
    ink: brandColors.slate,
    seed: brandPalette.blue[200],
    neutral: brandPalette.grey[300],
    link: brandPalette.indigo[500],
    accent: brandPalette.yellow[400],
    bifido: brandPalette.green[700],
    beneficial: brandPalette.green[500],
    variable: brandPalette.orange[500],
    unfriendly: brandPalette.red[500],
    unknown: brandPalette.grey[300],
    type1: brandPalette.green['700a'],
    type2: brandPalette.orange['500a'],
    type3: brandPalette.red['500a'],
    type4: brandPalette.yellow['500a'],
    type5: brandPalette.brown['500a'],
    type6: brandPalette.cadet['500a'],
    type7: brandPalette.indigo['500a'],
};
export const COLORS = {
    ...brandColors,
    ...palette,
    light: palette,
};
/**
 * Metric-tier tags snapshotted onto a kit at purchase time (HCP-Only Metrics).
 *
 * Shared rather than duplicated: the backend writes these values to
 * `kits.tags` and enforces the `default`-required guard against them, while
 * the ops dashboard renders the tag checklist from them. One definition means
 * a renamed tag cannot silently drift between the two.
 *
 * `default` is the universal baseline and must always be present. Dropping it
 * does not merely fail to add a tier - it hides every ungated metric, i.e. the
 * kit's entire baseline report.
 */
export const KIT_DEFAULT_TAG = 'default';
/** Tags ops may grant or revoke on a kit, with their operator-facing labels. */
export const KIT_ELEVATED_TAGS = [
    { tag: 'hcp_metrics', label: 'HCP Metrics' },
    { tag: 'legacy', label: 'Legacy (pre-cutover metric set)' },
];
export const CONSULT_ACTIVE_STATUS = ['rescheduled', 'scheduled'];
export const CONSULT_TYPES = {
    COACHING_PROGRAM: 'coaching_programs',
    FEEDBACK: 'feedback',
    INTRO_PRACTITIONER: 'intro_practitioner',
    RESEARCH_STUDY_PARTICIPANT: 'research_study_participant',
    RESULTS_REVIEW_AFFILIATE: 'results_review_affiliate',
    RESULTS_REVIEW_CUSTOMER: 'results_review_customer',
    RESULTS_REVIEW_PRACTITIONER: 'results_review_practitioner',
    GROUP_RESULTS_REVIEW_CUSTOMER: 'group_results_review_customer',
    ASYNC_REVIEW_VIDEO: 'async_review_video',
};
export const CONSULT_TYPE_MAP = {
    [CONSULT_TYPES.COACHING_PROGRAM]: 'Coaching Program',
    [CONSULT_TYPES.FEEDBACK]: 'Feedback',
    [CONSULT_TYPES.INTRO_PRACTITIONER]: 'Intro for Practitioner',
    [CONSULT_TYPES.RESEARCH_STUDY_PARTICIPANT]: 'Research Study',
    [CONSULT_TYPES.RESULTS_REVIEW_AFFILIATE]: 'Results Review (Affiliate)',
    [CONSULT_TYPES.RESULTS_REVIEW_CUSTOMER]: 'Results Review',
    [CONSULT_TYPES.RESULTS_REVIEW_PRACTITIONER]: 'Results Review (Practitioner)',
    [CONSULT_TYPES.GROUP_RESULTS_REVIEW_CUSTOMER]: 'Group Results Review',
    [CONSULT_TYPES.ASYNC_REVIEW_VIDEO]: 'Async Review Video',
};
export function hourToSeconds(hour) {
    return hour * 60 * 60;
}
export const PREGNANCY_AGE_BOUNDARY = 18; // used as < 18 or >= 18
export const additionalTimeInSeconds = hourToSeconds(1); // will be set to 1 hour
export const PLACEHOLDER = {
    firstName: 'Jane',
    lastName: 'Appleby',
    email: 'jane@example.com',
    new_email: 'jane@example.com',
    phone: '(555) 555-5555',
    new_phone: '(555) 555-5555',
    password: '••••••••',
    passwordVisible: 'a nice, long password',
    verification_code: '123 456',
    impersonateId: 'e.g., aabfs5-678afg-98afdg-896adfg',
};
export const AUTOLOGOUT_REASONS = {
    EXPIRED: 'EXPIRED',
    INACTIVE: 'INACTIVE',
};
export const RESERVED_SUBDOMAINS = [
    'account',
    'admin',
    'api',
    'app',
    'auth',
    'dev',
    'docs',
    'email',
    'help',
    'mail',
    'ops',
    'portal',
    'practitioner',
    'partner',
    'staging',
    'support',
    'test',
    'www',
];
export const KIT_PRICE = 169;
export const KIT_PRICE_LEGACY = 149;
export const KIT_PRICE_20_OFF = 229;
export const lowRelativeAbundanceLimit = 0.00005;
export const highRelativeAbundanceLimit = 0.0001;
export const EMAIL_VALIDATION_PATTERN = {
    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email',
};
//International
export const PHONE_VALIDATION_PATTERN = {
    value: /^\+?(\d{1,3})?[\s\-\.]?(\(?\d{1,4}\)?)?[\s\-\.]?\d{1,4}[\s\-\.]?\d{1,9}(\s*(ext|x|ext.)\s*\d{1,5})?$/,
    message: 'Please enter a valid phone number',
};
// US and Canadian
export const ZIPCODE_VALIDATION_PATTERN = {
    value: /^(?:\d{5}(?:-\d{4})?|[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d)$/i,
    message: 'Please enter a valid zip code',
};
export const practitionerCodes30 = [
    'ALLINA30',
    'LISAJOAN30',
    'FULLSPECTRUM30',
    'PRECISE30',
    'RESTORETOTHRIVE30',
    'HEALTHYMAMAS30',
    'ECOCHIC30',
    'GERBICHIRO30',
    'FOODWITHTHOUGHT30',
    'BREASTFEEDINGDIETITIAN30',
    'BLOSSOMPEDIATRICS30',
    'BETTERYOURBIOME30',
    'DRNINA30',
    'DOCTORCARMEL30',
    'KINGDOMHEALTH30',
    'BEBYBABY30',
    'PALEODIETITIAN30',
    'ALOHA30',
    'ZYNDA30',
    'FREELYFED30',
    'MILESTONES30',
    'MVHS30',
    'WELCH30',
    'GOLNIK30',
    'NOURISHEDMOTHERS30',
    'VIBRANTHEALTH30',
    'ALISHABANKSWELLNESS30',
    'LAURENHAUGHEYNUTRITION30',
    'LAURENFERRITERWELLNESS30',
    'MINDFULLYWELL30',
    'ORGANICCURE30',
    'REVIVE30',
    'JARARICE30',
];
export const QPCR_PRICE = 79;
export const UserRoles = {
    USER: 'user',
    PRACTITIONER: 'practitioner',
    PARTNER: 'partner',
    ADMIN: 'admin',
    IMPERSONATE: 'impersonate',
};
//practitioner kits by lookup code
export const PRACTITIONER_KITS_DISPLAY_NAMES = {
    GUT_HEALTH_TEST_PRACTITIONER: 'Gut Health Test',
    VAGINAL_HEALTH_TEST_PRACTITIONER: 'Vaginal Health Test',
    TINY_PRO_GUT_HEALTH_TEST: 'PRO Gut Health Test',
};
/**
 * FE-owned display labels for kit add-ons, keyed by `AddonType`. The BE only
 * supplies lookup_code/price, so display text lives here and is shared by the
 * practitioner portal, partner portal and ops dashboard.
 */
export const ADDON_LABEL = {
    qpcr_candida: 'Candida detection (qPCR)',
    qpcr_hpylori: 'H. pylori detection (qPCR)',
    ag_hpylori: 'H. pylori detection (antigen)',
};
export const ADDON_SECTION_HEADER = 'Enhanced detection add-ons:';
export const FulfillmentSubFilter = {
    AGED: 'Aged',
    PRIORITY_SHIPPING: 'Priority Shipping',
    CANADA: 'Canada',
    TPL_ARMBRUST: 'TPL_ARMBRUST',
    ALL_PARTNERS: 'All Partners',
};
export const FulfillmentType = {
    INTERNAL: 'INTERNAL',
    TPL_ARMBRUST: 'TPL_ARMBRUST',
};
export const TplStatus = {
    PENDING: 'pending',
    CREATED: 'created',
    FAILED: 'failed',
    SHIPPED: 'shipped',
    CANCELLED: 'cancelled',
    DELETED: 'deleted',
    ERROR: 'error',
};
export const RESENDABLE_TPL_STATUSES = new Set([
    TplStatus.DELETED,
    TplStatus.ERROR,
    TplStatus.FAILED,
]);
export const DELETABLE_TPL_STATUSES = new Set([
    TplStatus.PENDING,
    TplStatus.CREATED,
    TplStatus.FAILED,
]);
export const FulfillmentStatus = {
    PENDING: 'pending',
    DONE: 'done',
    CANCELLED: 'cancelled',
    NEEDS_REVIEW: 'needs_review',
};
export const PRO_CATEGORY_NAME = 'PRO practitioner markers';
export const MAX_SHIPPING_INFO_KITS = 200;
//# sourceMappingURL=constants.js.map